/* ========== CHART PARSER ORCHESTRATOR ==========
 * Walks every sheet, finds its drawing relationships, extracts charts
 * (standard or extended) and sparklines, then runs each through its
 * matching parser. Returns { sheetName → [chartObj, …] }.
 *
 * Implementation lives in parsers/charts/:
 *   - constants.js           — chart type maps, theme color map
 *   - colorResolver.js       — srgbClr/schemeClr/lumMod resolution
 *   - cellRefs.js            — A1 / sheet-range / zip-path helpers
 *   - workbookContext.js     — sheet path map, sharedStrings, cell reads
 *   - anchors.js             — drawing-anchor extraction
 *   - chartTypeDetector.js   — find chart-type elements + their attrs
 *   - seriesParser.js        — per-<c:ser> parsing (idx-aware)
 *   - standardChartParser.js — <c:chart> orchestrator
 *   - extendedChartParser.js — chartEx (Treemap/Sunburst/Funnel/…)
 *   - sparklineParser.js     — inline cell sparklines
 *   - namespaces.js          — OOXML namespace URI strings
 * ================================================ */

import { qLocalAll } from "./xmlUtils";
import { ANCHOR_NAMES } from "./charts/constants";
import { setThemeColors } from "./charts/colorResolver";
import { resolveZipPath } from "./charts/cellRefs";
import { loadWorkbookContext } from "./charts/workbookContext";
import { extractAnchorChart } from "./charts/anchors";
import { parseStandardChart } from "./charts/standardChartParser";
import { parseExtendedChart } from "./charts/extendedChartParser";
import { extractSparklinesForSheet } from "./charts/sparklineParser";

/**
 * Read all rels of type "drawing" from a sheet's .rels file. Returns an
 * array of resolved zip paths like ["xl/drawings/drawing1.xml", …].
 */
async function readSheetDrawingPaths(zip, sheetRelsPath) {
  const sheetRelsFile = zip.file(sheetRelsPath);
  if (!sheetRelsFile) return [];
  const xml = await sheetRelsFile.async("string");
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const paths = [];
  qLocalAll(doc, "Relationship").forEach(rel => {
    const type = rel.getAttribute("Type") || "";
    if (type.indexOf("/relationships/drawing") !== -1) {
      const target = rel.getAttribute("Target");
      const resolved = resolveZipPath(sheetRelsPath, target);
      if (resolved) paths.push(resolved);
    }
  });
  return paths;
}

/** rId → resolved target path for a drawing's .rels file. */
async function readDrawingRelsMap(zip, drawingPath) {
  const dParts = drawingPath.split("/");
  const dFname = dParts.pop();
  const drawingRelsPath = dParts.join("/") + "/_rels/" + dFname + ".rels";
  const file = zip.file(drawingRelsPath);
  if (!file) return {};
  const xml = await file.async("string");
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const map = {};
  qLocalAll(doc, "Relationship").forEach(rel => {
    const id = rel.getAttribute("Id");
    const target = rel.getAttribute("Target");
    const resolved = resolveZipPath(drawingRelsPath, target);
    if (id && resolved) map[id] = resolved;
  });
  return map;
}

/**
 * Pull every chart (and image-anchored chart, via AlternateContent) out of
 * one drawing XML. Each result has the chart's rId, its resolved zip path,
 * and its anchor range. The actual chart XML isn't parsed yet — that's
 * deferred to the dispatch loop below.
 */
async function extractChartsFromDrawing(zip, sheetName, drawingPath, chartIdxRef) {
  const drawingFile = zip.file(drawingPath);
  if (!drawingFile) return [];

  const drawingRelsMap = await readDrawingRelsMap(zip, drawingPath);
  const drawingXml = await drawingFile.async("string");
  const drawingDoc = new DOMParser().parseFromString(drawingXml, "application/xml");

  const chartObjs = [];
  ANCHOR_NAMES.forEach(name => {
    qLocalAll(drawingDoc, name).forEach(anchor => {
      const found = extractAnchorChart(anchor);
      if (!found) return;
      const chartPath = drawingRelsMap[found.ref.rId];
      if (!chartPath) return;
      chartObjs.push({
        id: "chart-" + sheetName + "-" + (chartIdxRef.value++),
        type: "chart",
        chartPath,
        isExtended: !!found.ref.isExtended,
        range: found.range,
      });
    });
  });
  return chartObjs;
}

/** Run the matching parser on one chart object and hydrate it in place. */
async function hydrateChart(chartObj, zip, ctx) {
  if (chartObj.isSparkline) return; // already populated

  const chartFile = zip.file(chartObj.chartPath);
  if (!chartFile) { chartObj.placeholder = true; return; }

  const chartXml = await chartFile.async("string");
  const chartDoc = new DOMParser().parseFromString(chartXml, "application/xml");

  let parsed = null;
  try {
    parsed = chartObj.isExtended
      ? await parseExtendedChart(chartDoc, zip, ctx)
      : parseStandardChart(chartDoc);
  } catch (err) {
    console.warn("[chartParser] Failed to parse chart", chartObj.chartPath, err);
  }

  if (!parsed) { chartObj.placeholder = true; return; }

  chartObj.title       = parsed.title || "Chart";
  chartObj.chartType   = parsed.chartType || "bar";
  chartObj.isCombo     = !!parsed.isCombo;
  chartObj.series      = parsed.series || [];
  chartObj.categories  = parsed.categories || [];
  chartObj.layoutId    = parsed.layoutId;
  chartObj.grouping    = parsed.grouping || "standard";
  chartObj.is3D        = !!parsed.is3D;
  chartObj.hasMarker   = !!parsed.hasMarker;
  chartObj.ofPieGeom   = parsed.ofPieGeom || null;
  chartObj.radarStyle  = parsed.radarStyle || null;
  chartObj.geography   = parsed.geography || null;
  chartObj.dataLabels  = parsed.dataLabels || null;
  chartObj.subtotals   = parsed.subtotals || null;
  chartObj.binning     = parsed.binning || null;
  chartObj.hierarchy   = parsed.hierarchy || null;
  chartObj.catLevels   = parsed.catLevels || null;
  chartObj.placeholder = chartObj.series.length === 0;
}

/**
 * Public entry point. Walks every sheet, finds every chart + sparkline,
 * parses them, and returns them grouped by sheet name.
 *
 * @param {JSZip}     zip          Loaded xlsx zip
 * @param {string[]?} themeColors  Workbook theme accent colors (hex, no #)
 *                                 — passed through to colorResolver so
 *                                 schemeClr can resolve to real hex.
 */
export async function extractChartsFromZip(zip, themeColors) {
  const chartsBySheet = {};
  setThemeColors(themeColors);

  try {
    const ctx = await loadWorkbookContext(zip);
    if (!Object.keys(ctx.sheetIdToName).length) return chartsBySheet;

    for (const sheetRel of Object.keys(ctx.sheetPathToName)) {
      const sheetName = ctx.sheetPathToName[sheetRel];
      const sheetFullPath = "xl/" + sheetRel;
      const parts = sheetRel.split("/");
      const fname = parts.pop();
      const sheetRelsPath = ("xl/" + parts.join("/") + "/_rels/" + fname + ".rels").replace(/\/\//g, "/");

      if (!chartsBySheet[sheetName]) chartsBySheet[sheetName] = [];

      // 1. Inline sparklines (always in the worksheet XML itself)
      try {
        const sparks = await extractSparklinesForSheet(zip, ctx, sheetFullPath, sheetName);
        sparks.forEach(s => chartsBySheet[sheetName].push(s));
      } catch (sErr) {
        console.warn("[chartParser] Sparkline extraction failed for", sheetName, sErr);
      }

      // 2. Embedded charts (via drawing relationships)
      const drawingPaths = await readSheetDrawingPaths(zip, sheetRelsPath);
      const chartIdxRef = { value: 0 };
      for (const drawingPath of drawingPaths) {
        const charts = await extractChartsFromDrawing(zip, sheetName, drawingPath, chartIdxRef);
        charts.forEach(c => chartsBySheet[sheetName].push(c));
      }
    }

    // 3. Parse every chart's XML in place — in parallel. Each hydration
    //    awaits zip file reads; serial awaits made workbooks with many
    //    charts pay full latency per chart.
    const hydrations = [];
    for (const sheetName in chartsBySheet) {
      for (const chartObj of chartsBySheet[sheetName]) {
        hydrations.push(hydrateChart(chartObj, zip, ctx));
      }
    }
    await Promise.all(hydrations);
  } catch (err) {
    console.warn("Custom chart extraction failed", err);
  }
  return chartsBySheet;
}
